import { useState, useEffect, useRef } from 'react';
import { bookingsAPI, roomsAPI, ledgerAPI, propertiesAPI } from '../lib/api';
import { getCurrencySymbol, CURRENCIES } from '../lib/currencies';
import { toast } from 'sonner';
import {
  X, Loader2, User, CalendarClock, DollarSign, Users, Hash, ArrowLeftRight,
  Percent, CreditCard, FileText, LogOut, History, Globe, Send,
  Clock, Printer, ChevronRight, LogIn, UserX, AlertTriangle,
} from 'lucide-react';

/* ═══ Tab definitions matching the image ═══ */
const TABS = [
  { id: 'guest_info',     label: 'Guest Info',      icon: User,            color: 'text-blue-600',    bg: 'bg-blue-50' },
  { id: 'check_in',       label: 'Check In',        icon: LogIn,           color: 'text-emerald-600', bg: 'bg-emerald-50' },
  { id: 'amend_stay',     label: 'Amend Stay',      icon: CalendarClock,   color: 'text-green-600',   bg: 'bg-green-50' },
  { id: 'change_rate',    label: 'Change Rate',     icon: DollarSign,      color: 'text-amber-600',   bg: 'bg-amber-50' },
  { id: 'add_pax',        label: 'Add Pax',         icon: Users,           color: 'text-blue-500',    bg: 'bg-blue-50' },
  { id: 'room_transfer',  label: 'Room Transfer',   icon: Hash,            color: 'text-sky-600',     bg: 'bg-sky-50' },
  { id: 'exchange_room',  label: 'Exchange Room',    icon: ArrowLeftRight,  color: 'text-orange-500',  bg: 'bg-orange-50' },
  { id: 'discount',       label: 'Discount',        icon: Percent,         color: 'text-purple-600',  bg: 'bg-purple-50' },
  { id: 'payment',        label: 'Payment',         icon: CreditCard,      color: 'text-emerald-600', bg: 'bg-emerald-50' },
  { id: 'receipt',        label: 'Receipt',         icon: FileText,        color: 'text-blue-600',    bg: 'bg-blue-50' },
  { id: 'folio_transfer', label: 'Transfer',        icon: Send,            color: 'text-indigo-600',  bg: 'bg-indigo-50' },
  { id: 'no_show',        label: 'No Show',         icon: UserX,           color: 'text-orange-600',  bg: 'bg-orange-50' },
  { id: 'checkout',       label: 'Checkout',        icon: LogOut,          color: 'text-red-600',     bg: 'bg-red-50' },
  { id: 'history',        label: 'History',         icon: History,         color: 'text-gray-600',    bg: 'bg-gray-50' },
];

/* ═══ Shared field styles ═══ */
const inputCls = 'w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none';
const labelCls = 'block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5';
const saveBtnCls = 'px-6 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 transition-colors';

/* ═══════════════════ Tab Panels ═══════════════════ */

function GuestInfoPanel({ booking, onSave }) {
  const [name, setName] = useState(booking.guest_name || '');
  const [email, setEmail] = useState(booking.guest_email || '');
  const [phone, setPhone] = useState(booking.guest_phone || '');
  const [saving, setSaving] = useState(false);
  const changed = name !== (booking.guest_name || '') || email !== (booking.guest_email || '') || phone !== (booking.guest_phone || '');

  const handle = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (name !== (booking.guest_name || '')) payload.guest_name = name;
      if (email !== (booking.guest_email || '')) payload.guest_email = email;
      if (phone !== (booking.guest_phone || '')) payload.guest_phone = phone;
      await onSave(booking.id, payload);
      toast.success('Guest info updated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="tab-guest-info">
      <div><label className={labelCls}>Guest Name</label><input className={inputCls} value={name} onChange={e => setName(e.target.value)} data-testid="guest-name-input" /></div>
      <div><label className={labelCls}>Email</label><input className={inputCls} type="email" value={email} onChange={e => setEmail(e.target.value)} data-testid="guest-email-input" /></div>
      <div><label className={labelCls}>Phone</label><input className={inputCls} type="tel" value={phone} onChange={e => setPhone(e.target.value)} data-testid="guest-phone-input" /></div>
      <button onClick={handle} disabled={!changed || saving} className={saveBtnCls} data-testid="save-guest-info-btn">
        {saving ? <Loader2 className="h-4 w-4 animate-spin inline" /> : 'Save Changes'}
      </button>
    </div>
  );
}

function CheckInPanel({ booking, onCheckin, onClose, onRefresh }) {
  const [roomNumber, setRoomNumber] = useState(booking.room_number || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [uncleanedWarning, setUncleanedWarning] = useState(false);

  if (booking.checked_in) {
    return (
      <div className="space-y-4" data-testid="tab-check-in">
        <div className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
          <LogIn className="h-5 w-5 text-emerald-600" />
          <div>
            <p className="font-semibold text-emerald-800">Already Checked In</p>
            <p className="text-sm text-emerald-600">Room {booking.room_number || '—'} &middot; {booking.checked_in_at ? new Date(booking.checked_in_at).toLocaleString() : ''}</p>
          </div>
        </div>
      </div>
    );
  }

  if (booking.status === 'no_show') {
    return (
      <div className="space-y-4" data-testid="tab-check-in">
        <div className="flex items-center gap-3 p-4 bg-orange-50 border border-orange-200 rounded-lg">
          <UserX className="h-5 w-5 text-orange-600" />
          <p className="font-semibold text-orange-800">This booking has been marked as No Show</p>
        </div>
      </div>
    );
  }

  const handleCheckin = async (force = false) => {
    if (!roomNumber.trim()) { setError('Room number is required'); return; }
    setSaving(true);
    setError(null);
    setUncleanedWarning(false);
    try {
      await bookingsAPI.checkin(booking.id, { room_number: roomNumber.trim(), force_uncleaned: force });
      toast.success(`Checked in to Room ${roomNumber}`);
      if (onRefresh) onRefresh();
      if (onClose) onClose();
    } catch (e) {
      const detail = e.response?.data?.detail || 'Check-in failed';
      if (e.response?.status === 409 && detail.includes('cleaned')) {
        setUncleanedWarning(true);
      } else {
        setError(detail);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="tab-check-in">
      <div>
        <label className={labelCls}>Room Number</label>
        <input
          className={inputCls}
          value={roomNumber}
          onChange={e => { setRoomNumber(e.target.value); setError(null); setUncleanedWarning(false); }}
          placeholder="e.g. 101, 2A"
          autoFocus
          data-testid="checkin-room-input"
        />
      </div>
      <div className="text-sm text-gray-500 space-y-1">
        <p><span className="font-medium text-gray-700">Guest:</span> {booking.guest_name}</p>
        <p><span className="font-medium text-gray-700">Room type:</span> {booking.room_type_name}</p>
        <p><span className="font-medium text-gray-700">Stay:</span> {booking.check_in} to {booking.check_out} ({booking.nights}N)</p>
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="checkin-error">
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}

      {uncleanedWarning && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-2" data-testid="checkin-uncleaned-warning">
          <div className="flex items-start gap-2 text-sm text-amber-800">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <p>Room <strong>{roomNumber}</strong> has not been marked as cleaned by housekeeping. Are you sure you want to proceed?</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => handleCheckin(true)} disabled={saving} className="px-4 py-2 text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded-lg disabled:opacity-50" data-testid="force-checkin-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin inline" /> : 'Check In Anyway'}
            </button>
            <button onClick={() => setUncleanedWarning(false)} className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg">
              Cancel
            </button>
          </div>
        </div>
      )}

      {!uncleanedWarning && (
        <button onClick={() => handleCheckin(false)} disabled={saving || !roomNumber.trim()} className="px-6 py-2.5 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg disabled:opacity-50 transition-colors" data-testid="confirm-checkin-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin inline" /> : 'Check In Guest'}
        </button>
      )}
    </div>
  );
}

function NoShowPanel({ booking, onClose, onRefresh }) {
  const [saving, setSaving] = useState(false);

  if (booking.status === 'no_show') {
    return (
      <div className="space-y-4" data-testid="tab-no-show">
        <div className="flex items-center gap-3 p-4 bg-orange-50 border border-orange-200 rounded-lg">
          <UserX className="h-5 w-5 text-orange-600" />
          <p className="font-semibold text-orange-800">This booking is already marked as No Show</p>
        </div>
      </div>
    );
  }

  if (booking.checked_in) {
    return (
      <div className="space-y-4" data-testid="tab-no-show">
        <div className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <LogIn className="h-5 w-5 text-blue-600" />
          <p className="font-semibold text-blue-800">Guest is already checked in — cannot mark as no show.</p>
        </div>
      </div>
    );
  }

  const handle = async () => {
    setSaving(true);
    try {
      await bookingsAPI.noShow(booking.id);
      toast.success('Booking marked as No Show');
      if (onRefresh) onRefresh();
      if (onClose) onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to mark as no show');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="tab-no-show">
      <div className="flex items-start gap-3 p-4 bg-orange-50 border border-orange-200 rounded-lg">
        <UserX className="h-5 w-5 text-orange-600 mt-0.5" />
        <div>
          <p className="font-semibold text-orange-800">Mark as No Show</p>
          <p className="text-sm text-orange-600 mt-1">This will mark <strong>{booking.guest_name}</strong>'s booking as a no-show. This action is logged in the booking history.</p>
        </div>
      </div>
      <div className="text-sm text-gray-500 space-y-1">
        <p><span className="font-medium text-gray-700">Room:</span> {booking.room_type_name}</p>
        <p><span className="font-medium text-gray-700">Expected:</span> {booking.check_in} to {booking.check_out}</p>
        <p><span className="font-medium text-gray-700">Ref:</span> {booking.booking_reference}</p>
      </div>
      <button onClick={handle} disabled={saving} className="px-6 py-2.5 text-sm font-semibold text-white bg-orange-600 hover:bg-orange-700 rounded-lg disabled:opacity-50 transition-colors" data-testid="confirm-no-show-btn">
        {saving ? <Loader2 className="h-4 w-4 animate-spin inline" /> : 'Confirm No Show'}
      </button>
    </div>
  );
}



function AmendStayPanel({ booking, onSave }) {
  const [checkIn, setCheckIn] = useState(booking.check_in);
  const [checkOut, setCheckOut] = useState(booking.check_out);
  const [saving, setSaving] = useState(false);
  const nights = (() => { if (!checkIn || !checkOut) return 0; return Math.max(0, Math.round((new Date(checkOut) - new Date(checkIn)) / 86400000)); })();
  const changed = checkIn !== booking.check_in || checkOut !== booking.check_out;

  const handle = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (checkIn !== booking.check_in) payload.check_in = checkIn;
      if (checkOut !== booking.check_out) payload.check_out = checkOut;
      await onSave(booking.id, payload);
      toast.success('Stay dates amended');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="tab-amend-stay">
      <div className="grid grid-cols-2 gap-4">
        <div><label className={labelCls}>Check-in</label><input type="date" className={inputCls} value={checkIn} onChange={e => setCheckIn(e.target.value)} data-testid="amend-checkin" /></div>
        <div><label className={labelCls}>Check-out</label><input type="date" className={inputCls} value={checkOut} onChange={e => setCheckOut(e.target.value)} data-testid="amend-checkout" /></div>
      </div>
      <div className="flex items-center gap-2 text-sm text-gray-600"><Clock className="h-4 w-4 text-gray-400" />{nights} night{nights !== 1 ? 's' : ''}{nights !== booking.nights && <span className="text-xs text-amber-600 font-medium">(was {booking.nights})</span>}</div>
      <button onClick={handle} disabled={!changed || saving || nights < 1} className={saveBtnCls} data-testid="save-amend-stay-btn">
        {saving ? <Loader2 className="h-4 w-4 animate-spin inline" /> : 'Save Changes'}
      </button>
    </div>
  );
}

function ChangeRatePanel({ booking, onSave }) {
  const sym = getCurrencySymbol(booking.currency || 'USD');
  const [amount, setAmount] = useState(booking.total_amount || 0);
  const [notes, setNotes] = useState(booking.notes || '');
  const [saving, setSaving] = useState(false);
  const changed = amount !== (booking.total_amount || 0) || notes !== (booking.notes || '');

  const handle = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (amount !== (booking.total_amount || 0)) payload.total_amount = amount;
      if (notes !== (booking.notes || '')) payload.notes = notes;
      await onSave(booking.id, payload);
      toast.success('Rate updated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="tab-change-rate">
      <div>
        <label className={labelCls}>Total Amount ({sym})</label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{sym}</span>
          <input type="number" className={`${inputCls} pl-8`} value={amount} onChange={e => setAmount(parseFloat(e.target.value) || 0)} min="0" step="0.01" data-testid="rate-amount-input" />
        </div>
      </div>
      <div><label className={labelCls}>Notes</label><textarea className={`${inputCls} resize-none`} rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Reason for rate change..." data-testid="rate-notes-input" /></div>
      <button onClick={handle} disabled={!changed || saving} className={saveBtnCls} data-testid="save-rate-btn">
        {saving ? <Loader2 className="h-4 w-4 animate-spin inline" /> : 'Save Changes'}
      </button>
    </div>
  );
}

function AddPaxPanel({ booking, onSave }) {
  const [adults, setAdults] = useState(booking.adults || booking.guest_count || 1);
  const [children, setChildren] = useState(booking.children || 0);
  const [saving, setSaving] = useState(false);
  const changed = adults !== (booking.adults || booking.guest_count || 1) || children !== (booking.children || 0);

  const handle = async () => {
    setSaving(true);
    try {
      await onSave(booking.id, { adults, children, guest_count: adults + children });
      toast.success('Pax updated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="tab-add-pax">
      <div className="grid grid-cols-2 gap-4">
        <div><label className={labelCls}>Adults</label><input type="number" className={inputCls} value={adults} onChange={e => setAdults(parseInt(e.target.value) || 1)} min="1" max="20" data-testid="pax-adults" /></div>
        <div><label className={labelCls}>Children</label><input type="number" className={inputCls} value={children} onChange={e => setChildren(parseInt(e.target.value) || 0)} min="0" max="10" data-testid="pax-children" /></div>
      </div>
      <p className="text-sm text-gray-500">Total guests: <span className="font-semibold text-gray-800">{adults + children}</span></p>
      <button onClick={handle} disabled={!changed || saving} className={saveBtnCls} data-testid="save-pax-btn">
        {saving ? <Loader2 className="h-4 w-4 animate-spin inline" /> : 'Save Changes'}
      </button>
    </div>
  );
}

function RoomTransferPanel({ booking, onSave }) {
  const [roomNumber, setRoomNumber] = useState(booking.room_number || '');
  const [saving, setSaving] = useState(false);
  const changed = roomNumber !== (booking.room_number || '');

  const handle = async () => {
    setSaving(true);
    try {
      await onSave(booking.id, { room_number: roomNumber });
      toast.success(`Room number updated to ${roomNumber}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="tab-room-transfer">
      <div>
        <label className={labelCls}>Current Room Number</label>
        <p className="text-sm text-gray-500 mb-3">{booking.room_number || 'Not assigned'}</p>
      </div>
      <div><label className={labelCls}>New Room Number</label><input className={inputCls} value={roomNumber} onChange={e => setRoomNumber(e.target.value)} placeholder="e.g. 101, 2A" data-testid="room-number-input" /></div>
      <button onClick={handle} disabled={!changed || !roomNumber || saving} className={saveBtnCls} data-testid="save-room-transfer-btn">
        {saving ? <Loader2 className="h-4 w-4 animate-spin inline" /> : 'Transfer Room'}
      </button>
    </div>
  );
}

function ExchangeRoomPanel({ booking, onSave }) {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await roomsAPI.list(booking.property_id);
        setRooms((res.data || []).filter(r => r.id !== booking.room_type_id && r.is_active !== false));
      } catch { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, [booking.property_id, booking.room_type_id]);

  const handle = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await onSave(booking.id, { room_type_id: selected.id });
      toast.success(`Room exchanged to ${selected.name}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-3" data-testid="tab-exchange-room">
      <p className="text-sm text-gray-500">Current: <span className="font-semibold text-gray-800">{booking.room_type_name}</span></p>
      {loading ? <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div> : rooms.length === 0 ? <p className="text-gray-400 text-sm py-4">No other rooms available</p> : (
        <div className="space-y-2 max-h-[220px] overflow-y-auto">
          {rooms.map(r => (
            <button key={r.id} onClick={() => setSelected(r)} className={`w-full text-left p-3 rounded-lg border-2 transition-all ${selected?.id === r.id ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-testid={`exchange-room-${r.id}`}>
              <div className="flex justify-between items-center">
                <div><p className="font-medium text-gray-900 text-sm">{r.name}</p><p className="text-xs text-gray-500">Max {r.max_occupancy} guests</p></div>
                <p className="font-semibold text-sm">{getCurrencySymbol(r.currency || 'USD')}{r.price_per_night}/night</p>
              </div>
            </button>
          ))}
        </div>
      )}
      <button onClick={handle} disabled={!selected || saving} className={saveBtnCls} data-testid="save-exchange-room-btn">
        {saving ? <Loader2 className="h-4 w-4 animate-spin inline" /> : 'Exchange Room'}
      </button>
    </div>
  );
}

function DiscountPanel({ booking, onSave }) {
  const sym = getCurrencySymbol(booking.currency || 'USD');
  const [amount, setAmount] = useState(booking.discount_amount || 0);
  const [reason, setReason] = useState(booking.discount_reason || '');
  const [saving, setSaving] = useState(false);

  const handle = async () => {
    setSaving(true);
    try {
      await onSave(booking.id, { discount_amount: amount, discount_reason: reason });
      toast.success('Discount applied');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="tab-discount">
      <div>
        <label className={labelCls}>Discount Amount ({sym})</label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{sym}</span>
          <input type="number" className={`${inputCls} pl-8`} value={amount} onChange={e => setAmount(parseFloat(e.target.value) || 0)} min="0" step="0.01" data-testid="discount-amount" />
        </div>
      </div>
      <div><label className={labelCls}>Reason</label><textarea className={`${inputCls} resize-none`} rows={2} value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason for discount..." data-testid="discount-reason" /></div>
      <p className="text-sm text-gray-500">Current total: <span className="font-semibold">{sym}{booking.total_amount}</span> → After discount: <span className="font-semibold text-emerald-600">{sym}{Math.max(0, (booking.total_amount || 0) - amount).toFixed(2)}</span></p>
      <button onClick={handle} disabled={amount <= 0 || saving} className={saveBtnCls} data-testid="save-discount-btn">
        {saving ? <Loader2 className="h-4 w-4 animate-spin inline" /> : 'Apply Discount'}
      </button>
    </div>
  );
}

function PaymentPanel({ booking }) {
  const bookingSym = getCurrencySymbol(booking.currency || 'USD');
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('cash');
  const [currency, setCurrency] = useState(booking.currency || 'USD');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [payments, setPayments] = useState([]);
  const [loadingPay, setLoadingPay] = useState(true);

  const paySym = getCurrencySymbol(currency);

  useEffect(() => {
    (async () => {
      try { const r = await ledgerAPI.getBookingLedger(booking.id); setPayments(r.data.payments || r.data || []); }
      catch { /* ignore */ }
      finally { setLoadingPay(false); }
    })();
  }, [booking.id]);

  const handleRecord = async () => {
    const val = parseFloat(amount);
    if (!val || val <= 0) return;
    setSaving(true);
    try {
      await ledgerAPI.recordPayment({ booking_id: booking.id, amount: val, method, note, currency });
      toast.success('Payment recorded');
      setAmount(''); setNote('');
      const r = await ledgerAPI.getBookingLedger(booking.id); setPayments(r.data.payments || r.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to record payment'); }
    finally { setSaving(false); }
  };

  const handleVoid = async (payId) => {
    try {
      await ledgerAPI.voidPayment(payId);
      toast.success('Payment voided');
      const r = await ledgerAPI.getBookingLedger(booking.id); setPayments(r.data.payments || r.data || []);
    } catch (e) { toast.error('Failed to void payment'); }
  };

  const totalPaid = payments.reduce((s, p) => s + (p.amount || 0), 0);
  const outstanding = (booking.total_amount || 0) - totalPaid;

  return (
    <div className="space-y-4" data-testid="tab-payment">
      <div className="grid grid-cols-3 gap-3 text-center">
        <div className="p-3 bg-gray-50 rounded-lg"><p className="text-[10px] font-semibold text-gray-400 uppercase">Total</p><p className="text-lg font-bold text-gray-900">{bookingSym}{(booking.total_amount || 0).toFixed(2)}</p></div>
        <div className="p-3 bg-emerald-50 rounded-lg"><p className="text-[10px] font-semibold text-emerald-500 uppercase">Paid</p><p className="text-lg font-bold text-emerald-700">{bookingSym}{totalPaid.toFixed(2)}</p></div>
        <div className={`p-3 rounded-lg ${outstanding > 0 ? 'bg-red-50' : 'bg-green-50'}`}><p className={`text-[10px] font-semibold uppercase ${outstanding > 0 ? 'text-red-500' : 'text-green-500'}`}>Outstanding</p><p className={`text-lg font-bold ${outstanding > 0 ? 'text-red-700' : 'text-green-700'}`}>{bookingSym}{outstanding.toFixed(2)}</p></div>
      </div>
      <div className="border-t pt-3">
        <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Record Payment</p>
        <div className="grid grid-cols-3 gap-3">
          <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{paySym}</span><input type="number" className={`${inputCls} pl-8`} value={amount} onChange={e => setAmount(e.target.value)} min="0" step="0.01" placeholder="0.00" data-testid="payment-amount" /></div>
          <select className={inputCls} value={method} onChange={e => setMethod(e.target.value)} data-testid="payment-method">
            <option value="cash">Cash</option><option value="card">Card</option><option value="bank_transfer">Bank Transfer</option><option value="qr">QR</option><option value="pos">POS</option><option value="online">Online</option>
          </select>
          <select className={inputCls} value={currency} onChange={e => setCurrency(e.target.value)} data-testid="payment-currency">
            {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.code} ({c.symbol})</option>)}
          </select>
        </div>
        <input className={`${inputCls} mt-2`} value={note} onChange={e => setNote(e.target.value)} placeholder="Note (optional)" data-testid="payment-note" />
        <button onClick={handleRecord} disabled={saving || !parseFloat(amount)} className={`${saveBtnCls} mt-3 w-full`} data-testid="record-payment-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin inline" /> : 'Record Payment'}
        </button>
      </div>
      {/* Payment history */}
      {loadingPay ? <Loader2 className="h-4 w-4 animate-spin text-gray-400 mx-auto" /> : payments.length > 0 && (
        <div className="border-t pt-3">
          <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Payment History</p>
          <div className="space-y-1.5 max-h-[140px] overflow-y-auto">
            {payments.map(p => (
              <div key={p.id} className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg text-sm">
                <div><span className="font-medium">{getCurrencySymbol(p.currency || booking.currency || 'USD')}{p.amount?.toFixed(2)}</span><span className="text-gray-400 mx-1.5">via</span><span className="text-gray-600">{p.method}</span>{p.currency && p.currency !== (booking.currency || 'USD') && <span className="ml-1 text-xs text-amber-500 font-medium">({p.currency})</span>}{p.note && <span className="text-gray-400 ml-1.5">— {p.note}</span>}</div>
                <button onClick={() => handleVoid(p.id)} className="text-xs text-red-500 hover:text-red-700 font-medium" data-testid={`void-payment-${p.id}`}>Void</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ReceiptPanel({ booking }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const receiptRef = useRef(null);

  useEffect(() => {
    (async () => {
      try { const r = await bookingsAPI.getReceipt(booking.id); setData(r.data); }
      catch { toast.error('Failed to load receipt'); }
      finally { setLoading(false); }
    })();
  }, [booking.id]);

  const handlePrint = () => {
    const el = receiptRef.current;
    if (!el) return;
    const win = window.open('', '_blank', 'width=700,height=900');
    win.document.write(`<html><head><title>Receipt - ${booking.booking_reference}</title><style>body{font-family:Arial,sans-serif;padding:24px;color:#333;font-size:13px}table{width:100%;border-collapse:collapse}th,td{padding:8px 6px;text-align:left;border-bottom:1px solid #e5e7eb}th{font-size:11px;text-transform:uppercase;color:#888}h2{margin:0 0 4px}p{margin:4px 0}.total-row td{font-weight:700;border-top:2px solid #333}</style></head><body>${el.innerHTML}</body></html>`);
    win.document.close();
    win.print();
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>;
  if (!data) return <p className="text-gray-400 text-center py-8">No receipt data</p>;

  const b = data.booking;
  const prop = data.property;
  const room = data.room;
  const payments = data.payments || [];
  const sym = getCurrencySymbol(b.currency || prop.currency || 'USD');
  const totalPaid = payments.reduce((s, p) => s + (p.amount || 0), 0);
  const outstanding = (b.total_amount || 0) - totalPaid;

  return (
    <div data-testid="tab-receipt">
      <div ref={receiptRef} className="space-y-4">
        <div className="text-center border-b pb-3">
          <h2 className="text-lg font-bold text-gray-900">{prop.name}</h2>
          <p className="text-xs text-gray-500">{prop.address}, {prop.city}, {prop.country}</p>
          <p className="text-xs text-gray-500">{prop.contact_email}</p>
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><span className="text-gray-400 text-xs">Booking Ref:</span><p className="font-mono font-semibold">{b.booking_reference}</p></div>
          <div><span className="text-gray-400 text-xs">Guest:</span><p className="font-medium">{b.guest_name}</p></div>
          <div><span className="text-gray-400 text-xs">Room:</span><p>{room.name}{b.room_number ? ` (#${b.room_number})` : ''}</p></div>
          <div><span className="text-gray-400 text-xs">Stay:</span><p>{b.check_in} to {b.check_out} ({b.nights}N)</p></div>
        </div>
        <table className="text-sm">
          <thead><tr><th>Description</th><th className="text-right">Amount</th></tr></thead>
          <tbody>
            <tr><td>Room: {room.name} x {b.nights} night{b.nights > 1 ? 's' : ''}</td><td className="text-right">{sym}{(b.total_amount || 0).toFixed(2)}</td></tr>
            {b.discount_amount > 0 && <tr><td className="text-red-500">Discount{b.discount_reason ? ` (${b.discount_reason})` : ''}</td><td className="text-right text-red-500">-{sym}{b.discount_amount.toFixed(2)}</td></tr>}
          </tbody>
        </table>
        {payments.length > 0 && (
          <table className="text-sm">
            <thead><tr><th>Payment</th><th>Method</th><th className="text-right">Amount</th></tr></thead>
            <tbody>
              {payments.map(p => <tr key={p.id}><td>{p.note || '—'}</td><td>{p.method}</td><td className="text-right">{sym}{p.amount?.toFixed(2)}</td></tr>)}
              <tr className="font-bold border-t-2 border-gray-900"><td colSpan={2}>Total Paid</td><td className="text-right">{sym}{totalPaid.toFixed(2)}</td></tr>
              {outstanding > 0 && <tr className="text-red-600"><td colSpan={2}>Outstanding Balance</td><td className="text-right">{sym}{outstanding.toFixed(2)}</td></tr>}
            </tbody>
          </table>
        )}
      </div>
      <button onClick={handlePrint} className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors" data-testid="print-receipt-btn">
        <Printer className="h-4 w-4" /> Print Receipt
      </button>
    </div>
  );
}

function FolioTransferPanel({ booking, properties, onTransfer }) {
  const available = (properties || []).filter(p => p.id !== booking.property_id);
  const [targetPropId, setTargetPropId] = useState('');
  const [rooms, setRooms] = useState([]);
  const [targetRoomId, setTargetRoomId] = useState('');
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!targetPropId) { setRooms([]); setTargetRoomId(''); return; }
    (async () => {
      setLoadingRooms(true);
      try { const r = await roomsAPI.list(targetPropId); setRooms((r.data || []).filter(r2 => r2.is_active !== false)); }
      catch { setRooms([]); }
      finally { setLoadingRooms(false); }
    })();
  }, [targetPropId]);

  const handle = async () => {
    if (!targetPropId) return;
    setSaving(true);
    try {
      await onTransfer(booking.id, { target_property_id: targetPropId, target_room_type_id: targetRoomId || undefined });
      toast.success('Booking transferred');
    } catch (e) { toast.error(e.response?.data?.detail || 'Transfer failed'); }
    finally { setSaving(false); }
  };

  if (available.length === 0) return <div className="text-center py-8 text-gray-400" data-testid="tab-folio-transfer"><Globe className="h-8 w-8 mx-auto mb-2 text-gray-300" /><p className="text-sm">Only one property available. Transfer requires multiple properties.</p></div>;

  return (
    <div className="space-y-4" data-testid="tab-folio-transfer">
      <p className="text-sm text-gray-500">Current property: <span className="font-semibold text-gray-800">{booking.property_name}</span></p>
      <div>
        <label className={labelCls}>Transfer To Property</label>
        <select className={inputCls} value={targetPropId} onChange={e => setTargetPropId(e.target.value)} data-testid="transfer-property-select">
          <option value="">Select property...</option>
          {available.map(p => <option key={p.id} value={p.id}>{p.name} ({p.city})</option>)}
        </select>
      </div>
      {targetPropId && (
        <div>
          <label className={labelCls}>Room Type at New Property</label>
          {loadingRooms ? <Loader2 className="h-4 w-4 animate-spin text-gray-400" /> : (
            <select className={inputCls} value={targetRoomId} onChange={e => setTargetRoomId(e.target.value)} data-testid="transfer-room-select">
              <option value="">Keep current / Select later</option>
              {rooms.map(r => <option key={r.id} value={r.id}>{r.name} — {getCurrencySymbol(r.currency || 'USD')}{r.price_per_night}/night</option>)}
            </select>
          )}
        </div>
      )}
      <button onClick={handle} disabled={!targetPropId || saving} className={saveBtnCls} data-testid="transfer-confirm-btn">
        {saving ? <Loader2 className="h-4 w-4 animate-spin inline" /> : 'Transfer Booking'}
      </button>
    </div>
  );
}

function CheckoutPanel({ booking, onCheckout }) {
  const [saving, setSaving] = useState(false);
  const isCheckedOut = booking.checked_out;

  const handle = async () => {
    setSaving(true);
    try {
      await onCheckout(booking.id);
      toast.success('Guest checked out');
    } catch (e) { toast.error(e.response?.data?.detail || 'Checkout failed'); }
    finally { setSaving(false); }
  };

  if (isCheckedOut) return <div className="text-center py-8" data-testid="tab-checkout"><LogOut className="h-8 w-8 text-gray-300 mx-auto mb-2" /><p className="text-sm text-gray-400">Guest has already been checked out.</p></div>;
  if (!booking.checked_in) return <div className="text-center py-8" data-testid="tab-checkout"><LogOut className="h-8 w-8 text-gray-300 mx-auto mb-2" /><p className="text-sm text-gray-400">Guest must be checked in before checkout.</p></div>;

  return (
    <div className="space-y-4" data-testid="tab-checkout">
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-sm font-medium text-red-800">Are you sure you want to check out <span className="font-bold">{booking.guest_name}</span>?</p>
        <p className="text-xs text-red-600 mt-1">Room: {booking.room_type_name}{booking.room_number ? ` (#${booking.room_number})` : ''}</p>
      </div>
      <button onClick={handle} disabled={saving} className="w-full px-6 py-3 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50 transition-colors" data-testid="checkout-confirm-btn">
        {saving ? <Loader2 className="h-4 w-4 animate-spin inline" /> : 'Confirm Checkout'}
      </button>
    </div>
  );
}

function HistoryPanel({ booking, propertyTimezone }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const r = await bookingsAPI.getHistory(booking.id); setHistory(r.data.history || []); }
      catch { setHistory([]); }
      finally { setLoading(false); }
    })();
  }, [booking.id]);

  const tz = propertyTimezone || 'UTC';
  const fmtTs = (iso) => { try { return new Date(iso).toLocaleString('en-GB', { timeZone: tz, day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }); } catch { return iso; } };

  const ACTION_LABELS = {
    booking_created: 'Booking Created', checked_in: 'Checked In', checked_out: 'Checked Out',
    undo_checkin: 'Check-in Undone', amended: 'Amended', status_changed: 'Status Changed',
    no_show: 'Marked No Show',
    booking_modified: 'Modified (Channex)', payment_recorded: 'Payment Recorded', payment_voided: 'Payment Voided',
    payment_status_changed: 'Payment Status Updated', channex_synced: 'Channex Synced', folio_transferred: 'Folio Transferred',
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>;

  return (
    <div data-testid="tab-history" className="max-h-[380px] overflow-y-auto">
      {history.length === 0 ? <p className="text-gray-400 text-center py-8 text-sm">No history yet</p> : (
        <div className="space-y-3">
          {history.map(e => (
            <div key={e.id} className="flex gap-3 text-sm" data-testid={`history-${e.id}`}>
              <div className="flex-shrink-0 w-2 h-2 mt-1.5 rounded-full bg-blue-400" />
              <div className="flex-1 min-w-0">
                <div className="flex justify-between items-start gap-2">
                  <p className="font-medium text-gray-900">{ACTION_LABELS[e.action] || e.action}</p>
                  <span className="text-[10px] text-gray-400 whitespace-nowrap">{fmtTs(e.timestamp)}</span>
                </div>
                <p className="text-xs text-gray-500">by {e.performed_by_name || e.performed_by_email || 'System'}</p>
                {e.details && Object.keys(e.details).filter(k => k !== 'source').length > 0 && (
                  <div className="mt-1 space-y-0.5">
                    {Object.entries(e.details).filter(([k]) => k !== 'source').map(([k, v]) => (
                      <p key={k} className="text-[11px] text-gray-400">
                        {k.replace(/_/g, ' ')}: {v && typeof v === 'object' && 'from' in v ? <><span className="line-through text-red-300">{String(v.from ?? '—')}</span> <span className="text-gray-300">→</span> <span className="text-emerald-500">{String(v.to ?? '—')}</span></> : String(v)}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════ Main Modal ═══════════════════ */

export function BookingActionModal({ booking: initialBooking, initialTab, properties, propertyTimezone, onAmend, onCheckout, onTransfer, onClose, onRefresh }) {
  const [activeTab, setActiveTab] = useState(initialTab || 'guest_info');
  const [booking, setBooking] = useState(initialBooking);

  const refreshBooking = async () => {
    try {
      const res = await bookingsAPI.get(booking.id);
      if (res.data) setBooking(prev => ({ ...prev, ...res.data }));
    } catch {}
    if (onRefresh) onRefresh();
  };

  const handleAmendAndRefresh = async (id, data) => {
    await onAmend(id, data);
    await refreshBooking();
  };

  const handleCheckoutAndRefresh = async (id) => {
    await onCheckout(id);
    if (onRefresh) onRefresh();
    onClose();
  };

  const handleTransferAndRefresh = async (id, data) => {
    await onTransfer(id, data);
    if (onRefresh) onRefresh();
    onClose();
  };

  const renderPanel = () => {
    switch (activeTab) {
      case 'guest_info': return <GuestInfoPanel booking={booking} onSave={handleAmendAndRefresh} />;
      case 'check_in': return <CheckInPanel booking={booking} onCheckin={handleAmendAndRefresh} onClose={onClose} onRefresh={refreshBooking} />;
      case 'amend_stay': return <AmendStayPanel booking={booking} onSave={handleAmendAndRefresh} />;
      case 'change_rate': return <ChangeRatePanel booking={booking} onSave={handleAmendAndRefresh} />;
      case 'add_pax': return <AddPaxPanel booking={booking} onSave={handleAmendAndRefresh} />;
      case 'room_transfer': return <RoomTransferPanel booking={booking} onSave={handleAmendAndRefresh} />;
      case 'exchange_room': return <ExchangeRoomPanel booking={booking} onSave={handleAmendAndRefresh} />;
      case 'discount': return <DiscountPanel booking={booking} onSave={handleAmendAndRefresh} />;
      case 'payment': return <PaymentPanel booking={booking} />;
      case 'receipt': return <ReceiptPanel booking={booking} />;
      case 'folio_transfer': return <FolioTransferPanel booking={booking} properties={properties} onTransfer={handleTransferAndRefresh} />;
      case 'no_show': return <NoShowPanel booking={booking} onClose={onClose} onRefresh={refreshBooking} />;
      case 'checkout': return <CheckoutPanel booking={booking} onCheckout={handleCheckoutAndRefresh} />;
      case 'history': return <HistoryPanel booking={booking} propertyTimezone={propertyTimezone} />;
      default: return null;
    }
  };

  const currentTabDef = TABS.find(t => t.id === activeTab) || TABS[0];

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="booking-action-modal">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b flex-shrink-0">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Booking Actions</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              <span className="font-medium text-gray-700">{booking.guest_name}</span>
              <span className="text-gray-400 ml-1.5">({booking.booking_reference})</span>
              <span className="text-gray-300 mx-1.5">|</span>
              <span>{booking.room_type_name}{booking.room_number ? ` #${booking.room_number}` : ''}</span>
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg" data-testid="action-modal-close"><X className="h-5 w-5 text-gray-400" /></button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Tab sidebar */}
          <div className="w-[200px] border-r bg-gray-50/50 overflow-y-auto flex-shrink-0">
            {TABS.map(tab => {
              const Icon = tab.icon;
              const active = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-2.5 px-4 py-3 text-left text-sm transition-colors ${
                    active ? 'bg-white border-r-2 border-blue-600 font-semibold text-gray-900' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  data-testid={`tab-btn-${tab.id}`}
                >
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${active ? tab.bg : 'bg-transparent'}`}>
                    <Icon className={`h-4 w-4 ${active ? tab.color : 'text-gray-400'}`} />
                  </div>
                  <span className="truncate">{tab.label}</span>
                </button>
              );
            })}
          </div>

          {/* Panel */}
          <div className="flex-1 overflow-y-auto p-5">
            {renderPanel()}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══ Quick action grid for menus ═══ */
export const BOOKING_ACTIONS = TABS.filter(t => t.id !== 'history').map(t => ({
  id: t.id, label: t.label, icon: t.icon, color: t.color, bg: t.bg,
}));
